/**
 * CUST005 sales-evaluation turnover UI helpers (BE #107 contract).
 * Shared color resolver for doughnut segments and top-customer dots.
 */

export const TURNOVER_KNOWN_SEGMENT_CODES = Object.freeze([
  'occasional_customer',
  'regular_customer',
  'strategic_customer',
  'lost_customer',
]);

export const TURNOVER_OTHER_SEGMENT_CODE = 'other';

const SHORT_TO_CANONICAL = Object.freeze({
  occasional: 'occasional_customer',
  regular: 'regular_customer',
  strategic: 'strategic_customer',
  lost: 'lost_customer',
  other: 'other',
  unclassified: 'other',
});

const SEGMENT_COLOR_META = Object.freeze({
  occasional_customer: { settingKey: 'turnover_occasional_color', fallbackColor: 'yellow' },
  regular_customer: { settingKey: 'turnover_regular_color', fallbackColor: 'orange' },
  strategic_customer: { settingKey: 'turnover_strategic_color', fallbackColor: 'green' },
  lost_customer: { settingKey: 'turnover_lost_color', fallbackColor: 'red' },
  other: { settingKey: null, fallbackColor: 'gray' },
});

export const COLOR_NAME_TO_HEX = Object.freeze({
  red: '#dc2626',
  orange: '#f97316',
  yellow: '#eab308',
  green: '#16a34a',
  blue: '#2563eb',
  purple: '#7c3aed',
  violet: '#7c3aed',
  gray: '#6b7280',
  black: '#111827',
});

export function colorNameToHex(color) {
  if (typeof color === 'string' && color.trim().startsWith('#')) {
    return color.trim();
  }
  return COLOR_NAME_TO_HEX[String(color || '').toLowerCase()] || '#6b7280';
}

/**
 * Deterministic normalize: API codes + short aliases → canonical segment code.
 */
export function normalizeTurnoverSegmentCode(raw) {
  const s = String(raw || '').toLowerCase().trim();
  if (!s) return TURNOVER_OTHER_SEGMENT_CODE;
  if (SHORT_TO_CANONICAL[s]) return SHORT_TO_CANONICAL[s];
  if (TURNOVER_KNOWN_SEGMENT_CODES.includes(s)) return s;
  if (s === TURNOVER_OTHER_SEGMENT_CODE) return TURNOVER_OTHER_SEGMENT_CODE;
  return TURNOVER_OTHER_SEGMENT_CODE;
}

export function turnoverSegmentColorMeta(raw) {
  const code = normalizeTurnoverSegmentCode(raw);
  return SEGMENT_COLOR_META[code] || SEGMENT_COLOR_META.other;
}

/**
 * @param {string} rawSegment
 * @param {(settingKey: string, fallback: string) => string} [getCrmColor]
 */
export function resolveTurnoverSegmentColor(rawSegment, getCrmColor) {
  const meta = turnoverSegmentColorMeta(rawSegment);
  if (typeof getCrmColor === 'function' && meta.settingKey) {
    return getCrmColor(meta.settingKey, meta.fallbackColor);
  }
  return colorNameToHex(meta.fallbackColor);
}

export function turnoverSegmentLabelKey(raw) {
  const code = normalizeTurnoverSegmentCode(raw);
  const map = {
    occasional_customer: 'CrmSales.Type.Occasional',
    regular_customer: 'CrmSales.Type.Regular',
    strategic_customer: 'CrmSales.Type.Strategic',
    lost_customer: 'CrmSales.Type.Lost',
    other: 'CrmSales.Type.Other',
  };
  return map[code] || 'CrmSales.Type.Other';
}

/**
 * Local calendar date (no toISOString / UTC reinterpretation).
 * @param {Date} d
 */
export function toLocalDateString(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Same period contract as sales evaluation fetch / drilldown.
 */
export function buildSalesEvaluationPeriodParams(timePeriod, date) {
  if (!timePeriod || timePeriod === 'Všetko' || !date) return {};
  if (timePeriod === 'Vlastné' && Array.isArray(date) && date.length >= 2) {
    const d0 = date[0] instanceof Date ? date[0] : new Date(date[0]);
    const d1 = date[1] instanceof Date ? date[1] : new Date(date[1]);
    if (!d0 || !d1 || Number.isNaN(d0.getTime()) || Number.isNaN(d1.getTime())) return {};
    return { time_period: timePeriod, date_from: toLocalDateString(d0), date_to: toLocalDateString(d1) };
  }
  if (timePeriod === 'Vlastné') return {};
  const d = Array.isArray(date) ? date[0] : date;
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return {};
  return { time_period: timePeriod, date: toLocalDateString(d) };
}

function endOfLocalMonth(d) {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0);
}

function endOfLocalYear(d) {
  return new Date(d.getFullYear(), 11, 31);
}

/**
 * Map sales-evaluation period → CRM ticket list created_at filter
 * (`range:YYYY-MM-DD:YYYY-MM-DD` contract on /api/crm_communication_tickets/paged).
 */
export function buildSalesEvaluationTicketListPeriodFilters(timePeriod, date) {
  if (!timePeriod || timePeriod === 'Všetko' || !date) return {};
  if (timePeriod === 'Vlastné' && Array.isArray(date) && date.length >= 2) {
    const d0 = date[0] instanceof Date ? date[0] : new Date(date[0]);
    const d1 = date[1] instanceof Date ? date[1] : new Date(date[1]);
    if (!d0 || !d1 || Number.isNaN(d0.getTime()) || Number.isNaN(d1.getTime())) return {};
    return { filter_created_at: `range:${toLocalDateString(d0)}:${toLocalDateString(d1)}` };
  }
  if (timePeriod === 'Vlastné') return {};
  const d = Array.isArray(date) ? date[0] : date;
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return {};
  if (timePeriod === 'Za deň') {
    const day = toLocalDateString(d);
    return { filter_created_at: `range:${day}:${day}` };
  }
  if (timePeriod === 'Za mesiac') {
    const from = toLocalDateString(new Date(d.getFullYear(), d.getMonth(), 1));
    const to = toLocalDateString(endOfLocalMonth(d));
    return { filter_created_at: `range:${from}:${to}` };
  }
  if (timePeriod === 'Za rok') {
    const from = toLocalDateString(new Date(d.getFullYear(), 0, 1));
    const to = toLocalDateString(endOfLocalYear(d));
    return { filter_created_at: `range:${from}:${to}` };
  }
  return {};
}

/**
 * CUST036 / CRM ticket-list drilldown from Sales Evaluation graphs.
 * Preserves trader + period universe; keeps raw overall_status codes.
 */
export function buildSalesEvaluationCrmListDrilldownRequest({
  statusRequest = {},
  timePeriod,
  date,
  selectedTraderId,
} = {}) {
  const out = { ...(statusRequest && typeof statusRequest === 'object' ? statusRequest : {}) };
  Object.assign(out, buildSalesEvaluationTicketListPeriodFilters(timePeriod, date));
  if (selectedTraderId) out.filter_trader_id = String(selectedTraderId);
  return out;
}

/** True when drilldown owns overall_status and must win over toolbar/mask filter_status. */
export function hasDrilldownOverallStatusFilter(initialRequestFilters) {
  if (!initialRequestFilters || typeof initialRequestFilters !== 'object') return false;
  const v = initialRequestFilters.filter_overall_status;
  return v != null && String(v).trim() !== '';
}

/**
 * BE skips filter_overall_status whenever filter_status is non-empty.
 * Drilldown overall_status must therefore clear competing filter_status.
 */
export function applyDrilldownOverallStatusPrecedence(extra, initialRequestFilters) {
  if (!extra || typeof extra !== 'object') return extra;
  if (!hasDrilldownOverallStatusFilter(initialRequestFilters)) return extra;
  const overall = String(initialRequestFilters.filter_overall_status).trim();
  extra.filter_overall_status = overall;
  delete extra.filter_status;
  return extra;
}

/**
 * Normalize BE widgets.turnover_by_segment (+ fallback keyed object).
 * @returns {Array<{code: string, amount: number, customer_count: number, company_ids: number[]}>}
 */
export function normalizeTurnoverBySegment(widgets) {
  const rich = widgets?.turnover_by_segment;
  if (Array.isArray(rich)) {
    return rich
      .map((row) => {
        const code = normalizeTurnoverSegmentCode(row?.code ?? row?.segment ?? '');
        const amount = Number(row?.amount ?? row?.turnover ?? 0) || 0;
        const customer_count = Number(row?.customer_count ?? 0) || 0;
        const company_ids = Array.isArray(row?.company_ids)
          ? row.company_ids.map((id) => Number(id)).filter((id) => id > 0)
          : [];
        return { code, amount, customer_count, company_ids };
      })
      .filter((row) => row.amount > 0 || row.customer_count > 0 || row.company_ids.length > 0);
  }

  const keyed = widgets?.turnoverBySegment || widgets?.turnover || {};
  if (!keyed || typeof keyed !== 'object' || Array.isArray(keyed)) return [];
  const out = [];
  const seen = new Set();
  for (const code of TURNOVER_KNOWN_SEGMENT_CODES) {
    const amount = Number(keyed[code] ?? keyed[code.replace('_customer', '')] ?? 0) || 0;
    if (amount <= 0) continue;
    seen.add(code);
    out.push({ code, amount, customer_count: 0, company_ids: [] });
  }
  for (const [raw, val] of Object.entries(keyed)) {
    const code = normalizeTurnoverSegmentCode(raw);
    if (seen.has(code)) continue;
    const amount = Number(val) || 0;
    if (amount <= 0) continue;
    seen.add(code);
    out.push({ code, amount, customer_count: 0, company_ids: [] });
  }
  return out;
}

/**
 * @param {Array<{code: string, amount: number, customer_count: number, company_ids: number[]}>} segments
 * @param {(key: string) => string} t
 * @param {(settingKey: string, fallback: string) => string} getCrmColor
 */
export function buildTurnoverChartData(segments, t, getCrmColor) {
  const rows = Array.isArray(segments) ? segments : [];
  const labels = rows.map((row) => t(turnoverSegmentLabelKey(row.code)));
  const colors = rows.map((row) => resolveTurnoverSegmentColor(row.code, getCrmColor));
  const data = rows.map((row) => Number(row.amount) || 0);
  return {
    labels,
    datasets: [{ data, backgroundColor: colors, hoverBackgroundColor: colors }],
    segments: rows,
  };
}

/**
 * Turnover drilldown — BE #107 identity contract.
 * MUST NOT send sales_evaluation_scope / filter_customer_type for turnover.
 */
export function buildTurnoverDrilldownRequestFilters({
  segmentCode,
  timePeriod,
  date,
  selectedTraderId,
}) {
  const filters = {
    sales_evaluation_turnover_segment: normalizeTurnoverSegmentCode(segmentCode),
    ...buildSalesEvaluationPeriodParams(timePeriod, date),
  };
  if (selectedTraderId) {
    filters.selected_trader_id = selectedTraderId;
  }
  return filters;
}

export function readTotalTurnover(widgets) {
  if (widgets?.total_turnover != null) {
    return Number(widgets.total_turnover) || 0;
  }
  // Never derive from topCustomers — sum segments only as last-resort display fallback.
  const segments = normalizeTurnoverBySegment(widgets);
  return segments.reduce((acc, row) => acc + (Number(row.amount) || 0), 0);
}
